import React from 'react';

import { Card, Text, Box, Flex, Image } from '@opendoor/bricks/core';
import { colors } from '@opendoor/bricks/theme/eero';
import { useDevice } from '@opendoor/bricks/hooks/useMedia';
import { Variant } from '@opendoor/bricks/system/theme';
import { ReactComponent as EmptyImage } from 'static/images/home1.svg';

import {
  OverviewHeaderSubtitle,
  IOverviewHeaderSubtitle,
} from 'shared/components/closingDash/OverviewHeaderSubtitle';
import { Timeline, Step } from '@opendoor/cinderblocks/platform/Timeline';

export interface OverviewHeaderProps {
  /** main title to display */
  title: React.ReactNode;
  /** subtitle to display */
  subtitle?: React.ReactNode;
  /** name of the current step for the Timeline component (embedded in this component) */
  currentStepName: string;
  /** url of the photo to display */
  photo: React.ReactNode;
  /** alt text of the photo to display */
  alt: string;
  /** array of subtitle items for the OverviewHeaderSubtitle component */
  overviewSubtitleItems: IOverviewHeaderSubtitle['items'];
  /** array of steps for the Timeline component (embedded in this component) */
  steps: Array<Step>;
  /** card variant used for container */
  variant?: Variant;
}

/**
 * OverviewHeader displays closing overview of the page.
 *
 * component-test-check-ignore
 */
export const OverviewHeader: React.FC<OverviewHeaderProps> = ({
  title,
  subtitle = undefined,
  currentStepName,
  photo,
  alt,
  overviewSubtitleItems,
  steps,
  variant = 'tertiary',
}) => {
  const { isMobile } = useDevice();
  return (
    <Card
      padding={0}
      variant={variant}
      borderRadius={isMobile ? 'none' : 'semiRounded'}
      role="group"
      aria-label="Overview card"
    >
      <Flex data-testid="overview-header">
        {!isMobile && photo && <DesktopThumbnail photo={photo} alt={alt} />}
        <Box py={[8, null, 7]} px={[7, null, 9]} width="100%" overflow="hidden">
          <Flex
            data-testid="overview-header-title"
            justifyContent="space-between"
            alignItems="center"
          >
            {typeof title === 'string' ? (
              <Text
                as="h2"
                fontSize={['s4', null, 's5']}
                lineHeight={['s4', null, 's5']}
                fontWeight="bold"
              >
                {title}
              </Text>
            ) : (
              title
            )}
            {isMobile && photo && <MobileThumbnail photo={photo} alt={alt} />}
          </Flex>
          {subtitle && (
            <Text fontSize="s0" color="neutrals90" pt={[3, null, 0]}>
              {subtitle}
            </Text>
          )}
          <OverviewHeaderSubtitle
            items={overviewSubtitleItems}
            my={[8, null, 7]}
            gridGap={[8, null, 7]}
          />
          {steps.length ? (
            <Timeline
              ariaLabel="Progress timeline"
              currentStepName={currentStepName}
              steps={steps}
              fadeoutColor={colors.neutrals10}
            />
          ) : null}
        </Box>
      </Flex>
    </Card>
  );
};

export const DesktopThumbnailWidth = 210;
export const DesktopThumbnailBorderRadius = 'semiRounded';

export const MobileThumbnailWidth = 56;
export const MobileThumbnailBorderRadius = 'roundedSquare';

type Thumbnail = Pick<OverviewHeaderProps, 'photo' | 'alt'>;

const DesktopThumbnailEmpty: React.FC<Pick<Thumbnail, 'photo'>> = ({ photo }) => {
  return photo ? (
    <>{photo}</>
  ) : (
    <Box
      height="100%"
      width={DesktopThumbnailWidth}
      borderTopLeftRadius={DesktopThumbnailBorderRadius}
      borderBottomLeftRadius={DesktopThumbnailBorderRadius}
    >
      <EmptyImage
        height="100%"
        style={{
          objectFit: 'cover',
        }}
      ></EmptyImage>
    </Box>
  );
};

/** DesktopThumbnail */
const DesktopThumbnail: React.FC<Thumbnail> = ({ photo, alt }) => {
  return (
    <Flex height="auto" minWidth={DesktopThumbnailWidth} aria-hidden>
      {typeof photo === 'string' ? (
        <Image
          height="auto"
          width={DesktopThumbnailWidth}
          src={photo}
          alt={alt}
          objectFit="cover"
          borderTopLeftRadius={DesktopThumbnailBorderRadius}
          borderBottomLeftRadius={DesktopThumbnailBorderRadius}
        />
      ) : (
        <DesktopThumbnailEmpty photo={photo} />
      )}
    </Flex>
  );
};

const MobileEmptyThumbnail: React.FC<Pick<Thumbnail, 'photo'>> = ({ photo }) => {
  return photo ? (
    <>{photo}</>
  ) : (
    <Box height="100%" width="auto" borderRadius={MobileThumbnailBorderRadius} overflow="hidden">
      <EmptyImage
        height="auto"
        width="100%"
        style={{
          objectFit: 'cover',
        }}
      ></EmptyImage>
    </Box>
  );
};

/** MobileThumbnail */
const MobileThumbnail: React.FC<Thumbnail> = ({ photo, alt }) => {
  return (
    <Flex height={MobileThumbnailWidth} width={MobileThumbnailWidth} ml={4}>
      {typeof photo === 'string' ? (
        <Image
          height="100%"
          width="auto"
          src={photo}
          alt={alt}
          objectFit="cover"
          borderRadius={MobileThumbnailBorderRadius}
        />
      ) : (
        <MobileEmptyThumbnail photo={photo} />
      )}
    </Flex>
  );
};
